/**
 * Общие блоки профиля человека (изменение v1.5).
 *
 * Свой профиль (U-01), предпросмотр «как меня видят» (U-02) и профиль другого
 * человека (P-04) собираются из ОДНИХ И ТЕХ ЖЕ блоков — как в Telegram, где
 * «Мой профиль» и профиль контакта отличаются только набором действий:
 *
 *   ProfileHero      круглый аватар по центру, имя с возрастом, строка статуса;
 *                    нажатие разворачивает фотографии на всю ширину карточки
 *   ProfileActions   ряд плиток-действий (иконка сверху, подпись снизу)
 *   ProfileInfo      карточка сведений «подпись сверху, значение снизу»
 *   ProfileSection   секция с заголовком: интересы, форматы, ритм, подбор
 *
 * Раньше два экрана рисовали профиль по-своему (чужой — большой карточкой
 * 4:5, свой — аватаром и плитками) и расходились при каждой правке.
 */
import type { CSSProperties, ReactNode } from 'react'

import type { PublicPhoto } from '@shared/api/types'
import { useImageTone } from '@shared/lib/image-tone'
import { Avatar, Icon, cx, type IconName } from '@shared/ui'

import styles from './profile-view.module.css'

/** Тон строки статуса: нейтральный, «всё хорошо», «есть что исправить». */
export type ProfileStatusTone = 'default' | 'success' | 'warning'

function photoUrl(photo: PublicPhoto | undefined): string | null {
  if (!photo) return null
  return photo.variants.find((v) => v.name === 'medium')?.url ?? photo.variants[0]?.url ?? null
}

/**
 * Палитры обложки без фото — как цвета профилей в Telegram. Первая — фирменная
 * синяя «Движа»; остальные выбираются по id, чтобы у человека цвет не менялся.
 */
const COVER_PALETTES: readonly [string, string][] = [
  ['#0033ff', '#4d7bff'],
  ['#5b2fe0', '#9a73ff'],
  ['#0b7f86', '#2fc0b0'],
  ['#d9480f', '#ff9149'],
  ['#c2255c', '#ff6f9c'],
  ['#2b8a3e', '#5fcf7c'],
  ['#1864ab', '#3bb4f2'],
]

function hashOf(seed: string): number {
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0
  return Math.abs(hash)
}

/** Цвета обложки: подложка градиентом, сила затемнения под текстом и цвет верха. */
export interface CoverPalette {
  from: string
  to: string
  /** Непрозрачность затемнения поверх размытого снимка, 0…1. */
  shade: number
  /** Цвет у верхнего края — для системной области над прозрачной шапкой. */
  top: string
  /** Размытый снимок под подложкой; нет фото — только градиент. */
  photoUrl: string | null
}

/**
 * Обложка подстраивается под аватар (изменение v1.15): фон — сам снимок,
 * размытый, а подложка и затемнение — по его среднему цвету и яркости.
 */
export function useCoverPalette(
  photos: readonly PublicPhoto[],
  seed: string | null | undefined,
): CoverPalette {
  const url = photoUrl(photos[0])
  const tone = useImageTone(url)

  if (url && tone) {
    // Светлый снимок под белым текстом требует больше тени, тёмный — почти не требует
    const shade = Math.min(0.52, Math.max(0.14, 0.12 + tone.luminance * 0.55))
    return { from: tone.color, to: tone.deep, shade, top: tone.deep, photoUrl: url }
  }

  const [from, to] = COVER_PALETTES[seed ? hashOf(seed) % COVER_PALETTES.length : 0] ?? ['#0033ff', '#4d7bff']
  return { from, to, shade: url ? 0.28 : 0.08, top: from, photoUrl: url }
}

export function ProfileHero({
  name,
  age = null,
  photos,
  status,
  statusTone = 'default',
  palette,
  onPhotoClick,
}: {
  name: string | null
  age?: number | null
  photos: readonly PublicPhoto[]
  /** Строка под именем: «в сети», «Анкету показывают», телефон и никнейм. */
  status?: ReactNode
  statusTone?: ProfileStatusTone
  palette: CoverPalette
  /** Нажатие на аватар — просмотр фотографий (номер снимка). */
  onPhotoClick?: (index: number) => void
}): React.ReactElement {
  const avatar = (
    <Avatar name={name} photo={photos[0] ?? null} size="2xl" className={styles['coverAvatarImage']} />
  )

  return (
    <header
      className={styles['cover']}
      style={
        {
          '--cover-from': palette.from,
          '--cover-to': palette.to,
          '--cover-shade': palette.shade,
        } as CSSProperties
      }
    >
      {palette.photoUrl ? (
        <img className={styles['coverBackdrop']} src={palette.photoUrl} alt="" aria-hidden />
      ) : null}
      <span className={styles['coverShade']} aria-hidden />

      <div className={styles['coverBody']}>
        {photos.length && onPhotoClick ? (
          <button
            type="button"
            className={styles['coverAvatar']}
            aria-label={photos.length > 1 ? `Фотографии: ${photos.length}` : 'Показать фото'}
            onClick={() => onPhotoClick(0)}
          >
            {avatar}
            {photos.length > 1 ? (
              <span className={styles['photoCount']} aria-hidden>
                <Icon name="image" size={12} />
                {photos.length}
              </span>
            ) : null}
          </button>
        ) : (
          <span className={styles['coverAvatar']}>{avatar}</span>
        )}

        <h2 className={styles['coverName']}>
          {name ?? 'Без имени'}
          {age !== null && age !== undefined ? <span className={styles['coverAge']}>, {age}</span> : null}
        </h2>
        {status ? (
          <p className={cx(styles['coverStatus'], statusTone === 'warning' && styles['coverStatusWarning'])}>
            {status}
          </p>
        ) : null}
      </div>
    </header>
  )
}

/** Ряд плиток-действий под шапкой (call/video/mute/search в Telegram). */
export function ProfileActions({ children }: { children: ReactNode }): React.ReactElement {
  return <div className={styles['tiles']}>{children}</div>
}

export function ProfileAction({
  icon,
  label,
  disabled = false,
  onClick,
}: {
  icon: IconName
  label: string
  disabled?: boolean
  onClick: () => void
}): React.ReactElement {
  return (
    <button type="button" className={styles['tile']} disabled={disabled} onClick={onClick}>
      <Icon name={icon} size={22} />
      <span className={styles['tileLabel']}>{label}</span>
    </button>
  )
}

/** Карточка сведений: никнейм, «о себе», город, род занятий, языки. */
export function ProfileInfo({ children }: { children: ReactNode }): React.ReactElement {
  return <div className={styles['infoCard']}>{children}</div>
}

export function ProfileInfoRow({
  label,
  value,
  accent = false,
  multiline = false,
  note,
  trailingIcon,
  onClick,
}: {
  label: string
  value: ReactNode
  /** Значение-ссылка (никнейм) — акцентным цветом. */
  accent?: boolean
  multiline?: boolean
  /** Пометка под значением: «на проверке». */
  note?: string
  trailingIcon?: IconName
  onClick?: () => void
}): React.ReactElement {
  const content = (
    <>
      <span className={styles['infoText']}>
        <span className={styles['infoLabel']}>{label}</span>
        <span
          className={cx(styles['infoValue'], accent && styles['infoAccent'], multiline && styles['infoWrap'])}
        >
          {value}
        </span>
        {note ? <span className={styles['infoNote']}>{note}</span> : null}
      </span>
      {trailingIcon ? (
        <span className={styles['infoTrailing']} aria-hidden>
          <Icon name={trailingIcon} size={18} />
        </span>
      ) : null}
    </>
  )

  return onClick ? (
    <button type="button" className={styles['infoRow']} onClick={onClick}>
      {content}
    </button>
  ) : (
    <div className={styles['infoRow']}>{content}</div>
  )
}

export function ProfileSection({
  title,
  action,
  children,
}: {
  title: string
  action?: ReactNode
  children: ReactNode
}): React.ReactElement {
  return (
    <section className={styles['section']}>
      <div className={styles['sectionHead']}>
        <h3 className={styles['sectionTitle']}>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  )
}

export function ProfileChips({ children }: { children: ReactNode }): React.ReactElement {
  return <div className={styles['chips']}>{children}</div>
}
